import React, { useEffect, useState } from "react";
import { StyledHeader } from "../Common/styles/header";
import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import QuestionItemIndex from "./QuestionItemIndex";
import MDEditor from "@uiw/react-md-editor";
import rehypeSanitize from "rehype-sanitize";
import { errorToastMessage } from "../../utils/toast";
import http from "../../utils/http";
import { requiredStyles } from "../Common/styles/form";
import { DateTime } from "luxon";
import CompareFileWrapper from "./CompareFileWrapper";
import BackButton from "../Common/UI/BackButton";

const DisplayResponse = ({
  response,
  label,
  questionType,
  type,
  questionProperties,
}: any) => {
  return (
    <Stack gap={1} flex={1}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          mb: "10px",
        }}
      >
        <Typography variant="body1" fontWeight="medium">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="regular">
          {response?.createdAt}
        </Typography>
      </Stack>
      {questionType === "upload_file" ? (
        <Stack flexDirection={"row"} gap={1} flexWrap={"wrap"}>
          {response?.value?.map((res: any, index: number) => (
            <CompareFileWrapper
              res={res}
              responseId={response?.id}
              index={index}
              type={type}
            />
          ))}
        </Stack>
      ) : questionType === "address" ? (
        <Box
          sx={{
            borderRadius: "8px",
            p: "12px 16px",
            border: "1px solid",
            borderColor: response?.color?.borderColor,
            backgroundColor: response?.color?.bgColor,
            minHeight: "48px",
          }}
        >
          <Stack gap={0.5}>
            {questionProperties?.zipcode && (
              <Typography variant="body1" fontWeight="regular">
                Zip Code: {response?.value?.zipcode || ""}
              </Typography>
            )}
            {questionProperties?.city && (
              <Typography variant="body1" fontWeight="regular">
                City: {response?.value?.city || ""}
              </Typography>
            )}
            {questionProperties?.state && (
              <Typography variant="body1" fontWeight="regular">
                State: {response?.value?.state || ""}
              </Typography>
            )}
            {questionProperties?.country && (
              <Typography variant="body1" fontWeight="regular">
                Country: {response?.value?.country || ""}
              </Typography>
            )}
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: "8px",
            p: "12px 16px",
            border: "1px solid",
            borderColor: response?.color?.borderColor,
            backgroundColor: response?.color?.bgColor,
            minHeight: "48px",
          }}
        >
          <Typography variant="body1" fontWeight="regular">
            {response?.value}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

const GREEN_COLOR = {
  bgColor: "#DEF7EC",
  borderColor: "#4CAF50",
};

const RED_COLOR = {
  bgColor: "#FDF2F2",
  borderColor: "#F98080",
};

const DEFAULT_COLOR = {
  bgColor: "#FFFFFF",
  borderColor: "#E7E7E7",
};

const getResponse = (type: string, res: any) => {
  let formattedData: any = {
    id: res?.[0]?.id,
    createdAt: res?.[0]?.createdAt
      ? DateTime.fromISO(res?.[0]?.createdAt).toFormat("dd/MM/yyyy, hh:mm a")
      : "",
    version: res?.[0]?.version || 0,
    color: DEFAULT_COLOR,
  };

  if (type === "text" || type === "date") {
    formattedData["value"] = res?.[0]?.textValue || "";
  } else if (type === "number" || type === "slider") {
    formattedData["value"] = res?.[0]?.numberValue ?? "";
  } else if (type === "radio" || type === "dropdown") {
    const response = res?.[0];
    const label = response?.questionChoice?.label || "";
    const value = response?.textValue || "";
    formattedData["value"] = value ? `${label} - ${value}` : label;
  } else if (type === "checkbox") {
    const response = res
      ?.map((item: any) => {
        const label = item?.questionChoice?.label || "";
        const value = item?.textValue || "";
        return value ? `${label} - ${value}` : label;
      })
      .join(", ");
    formattedData["value"] = response;
  } else if (type === "calculated_field") {
    formattedData["value"] = res?.[0]?.textValue ?? res?.[0]?.numberValue ?? "";
  } else if (type === "upload_file") {
    formattedData["value"] = res?.[0]?.files || [];
  } else if (type === "address") {
    const addressData = res?.[0]?.gridValue || {};
    formattedData["value"] = addressData;
  }

  return formattedData;
};

interface CompareResponsesProps {
  studyId?: string;
  participantId: string;
  formId: string;
  formName: string;
  type: "study" | "registry";
}

const CompareResponses = ({
  studyId,
  participantId,
  formId,
  formName,
  type,
}: CompareResponsesProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState<any>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let url;
        if (type === "study") {
          url = `/study/${studyId}/admin-screening-responses/attempts?participantId=${participantId}`;
        } else {
          url = `/registry/admin-screening-responses/attempts?participantId=${participantId}`;
        }
        const res = await http.get(url);

        const attempts = res.data?.data;
        const questions = [];
        const participantRes: any[] = [];
        let participantQtnVisibilities: any = null;
        const adminRes: any[] = [];
        let adminQtnVisibilities: any = null;

        for (let i = 0; i < attempts.length; i++) {
          const attempt = attempts[i];

          let attemptUrl;
          if (type === "study") {
            attemptUrl = `/study/${studyId}/admin-screening-responses/${formId}?participantId=${participantId}&screeningAttemptId=${attempt.id}`;
          } else {
            attemptUrl = `/registry/admin-screening-responses/${formId}?participantId=${participantId}&screeningAttemptId=${attempt.id}`;
          }

          const { data } = await http.get(attemptUrl);

          const resData = data?.data;

          if (!attempt?.actorId) {
            participantRes.push(...resData?.responses);
            questions.push(...resData?.form?.questions);
            participantQtnVisibilities = resData?.questionVisibilities;
          } else {
            adminRes.push(...resData?.responses);
            adminQtnVisibilities = resData?.questionVisibilities;
          }
        }

        const formattedQuestions = questions
          ?.filter((question: any) => {
            const pid = question.id;
            const visibleToParticipant =
              participantQtnVisibilities?.[pid] !== false;
            const visibleToAdmin = adminQtnVisibilities?.[pid] !== false;
            return visibleToParticipant || visibleToAdmin;
          })
          ?.sort((a: any, b: any) => a.position - b.position)
          ?.map((question: any) => {
            const ptpRes = participantRes?.filter(
              (item) => item.questionId === question.id
            );
            const admRes = adminRes?.filter(
              (item) => item.questionId === question.id
            );

            const formattedParticipantRes =
              getResponse(question.type, ptpRes) || null;
            const formattedAdminRes =
              getResponse(question.type, admRes) || null;

            if (formattedAdminRes?.version > 1) {
              formattedParticipantRes["color"] = RED_COLOR;
              formattedAdminRes["color"] = GREEN_COLOR;
            } else if (
              !formattedParticipantRes?.version &&
              !!formattedAdminRes?.version
            ) {
              formattedParticipantRes["color"] = RED_COLOR;
              formattedAdminRes["color"] = GREEN_COLOR;
            } else if (
              !formattedAdminRes?.version &&
              !!formattedParticipantRes?.version
            ) {
              formattedParticipantRes["color"] = GREEN_COLOR;
              formattedAdminRes["color"] = RED_COLOR;
            }

            return {
              id: question.id,
              type: question.type,
              label: question.label,
              properties: question.properties,
              participantRes: formattedParticipantRes,
              adminRes: formattedAdminRes,
            };
          });

        setQuestions(formattedQuestions);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    fetchData();
  }, [setLoading, studyId, participantId, formId, type]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <StyledHeader>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <BackButton onClick={handleBack} />
          <Typography variant="h2" color="text.primary">
            Answer Modification History
          </Typography>
        </Box>
      </StyledHeader>
      <Box sx={{ p: 3, height: "calc(100% - 72px)", overflowY: "auto" }}>
        {!loading ? (
          <Paper
            sx={{
              width: "100%",
              p: 2,
              borderRadius: "12px",
            }}
          >
            <Stack
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                pb: 2,
                mb: 2,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack gap={1}>
                <Typography fontWeight={"medium"} variant="subtitle1">
                  {formName || ""}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={"regular"}
                  color="text.secondary"
                >
                  Screening
                </Typography>
              </Stack>
            </Stack>
            <Stack gap={2}>
              {questions?.map((question: any, index: number) => {
                return (
                  <Box
                    key={question.id}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "#E7E7E7",
                      borderRadius: "12px",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    <Stack direction="row" alignItems={"flex-start"} gap={2}>
                      <QuestionItemIndex index={index} question={question} />
                      <Box sx={{ flex: 1 }}>
                        <Box
                          mt={
                            ["summary", "statement"].includes(question?.type)
                              ? 0
                              : 1
                          }
                          sx={
                            question.properties?.required ? requiredStyles : {}
                          }
                        >
                          <MDEditor.Markdown
                            wrapperElement={{
                              "data-color-mode": "light",
                            }}
                            source={question.label}
                            rehypePlugins={[rehypeSanitize]}
                            style={{ fontWeight: "400", fontSize: "14px" }}
                          />
                        </Box>
                        {!["statement", "summary"].includes(question?.type) && (
                          <Stack direction="row" gap={3} mb={1} mt={5}>
                            <DisplayResponse
                              response={question?.participantRes}
                              questionType={question?.type}
                              label="Participant"
                              type={type}
                              questionProperties={question?.properties}
                            />
                            <DisplayResponse
                              response={question?.adminRes}
                              questionType={question?.type}
                              label="Admin"
                              type={type}
                              questionProperties={question?.properties}
                            />
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </>
  );
};

export default CompareResponses;
